import { Navigate, useLocation } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useRef } from 'react';

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isLoading: adminLoading } = useAdmin();
  const location = useLocation();
  const { toast } = useToast();
  const hasShownToast = useRef(false);
  
  // Track if we've ever successfully authenticated - prevents flash on tab switch
  const hasEverAuthenticated = useRef(false);
  
  // Once user is authenticated as admin, remember it
  if (user && isAdmin && !authLoading && !adminLoading) {
    hasEverAuthenticated.current = true;
  }

  const isLoading = authLoading || adminLoading;

  useEffect(() => {
    if (!isLoading && !isAdmin && user && !hasShownToast.current) {
      hasShownToast.current = true;
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to access this page.',
        variant: 'destructive',
      });
    }
  }, [isLoading, isAdmin, user, toast]);

  // Show loading only on initial auth check when we've never authenticated
  if (isLoading && !hasEverAuthenticated.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  return <>{children}</>;
}
